document.addEventListener("DOMContentLoaded", function () {
  Object.assign(window.GzTheme, {
    // Starts every console message, so Datadog can find ours.
    CONSOLE_PREFIX: "[WARRANTY LIFE]",
    // Marks our own cart requests, so other scripts' stand out.
    OWN_REQUEST_MARK: "?gz=1",
    CART_LOCK_TIMEOUT_MS: 15000,
    // How long pages keep re-checking after a repaired change.
    RECHECK_WINDOW_MS: 10000,
    _cartChanged: false,

    // Sends one cart change and reports it if it fails.
    // A 422 is reported: a sold-out plan breaks every add.
    postCart: async function (kind, endpoint, payload, failureText) {
      const body = { ...payload, sections: this.getSectionIds().join(","), sections_url: window.location.pathname };
      let status = null;
      let data = null;

      try {
        const res = await fetch(`${endpoint}${this.OWN_REQUEST_MARK}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        status = res.status;
        data = await res.json().catch(() => null);
        if (!res.ok || !data) throw new Error(data?.description || `${failureText} — status ${status}`);

        if (kind !== "update" || Object.keys(payload.updates || {}).length) this._cartChanged = true;
        if ("item_count" in data) this.CART_DATA = data;
        return data;
      } catch (error) {
        this.reportError(`cart ${kind} request failed`, error, {
          status: status ?? "no response",
          store_message: data?.description || data?.message,
          sent: payload,
        });
        throw error;
      }
    },

    // Adds the given items to the cart.
    addToCart: function (payload) {
      return this.postCart("add", this.CART_ADD_ENDPOINT, payload, "Failed to add to cart");
    },

    // Updates or changes cart lines, choosing the endpoint to match.
    updateCart: function (endpoint, payload) {
      const kind = endpoint.includes("change") ? "change" : "update";
      return this.postCart(kind, endpoint, payload, "Failed to update cart");
    },

    // Reads the current cart from Shopify.
    fetchCart: async function () {
      const endpoint = this.CART_JSON_ENDPOINT;
      let status = null;

      try {
        const response = await fetch(endpoint);
        status = response.status;
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.items) {
          const error = new Error(`Failed to fetch cart — status ${status}`);
          error.responseData = data;
          error.endpoint = endpoint;
          throw error;
        }
        this.CART_DATA = data;
        return data;
      } catch (error) {
        this.reportError("cart fetch failed", error, { status: status ?? "no response" });
        throw error;
      }
    },

    // Finds another line of this phone with the same plan.
    productHasWarrantyInCart: async function (cartItems, productId, productKey, warrantyId) {
      const productInCart = cartItems.find((item) =>
        String(item.key) !== String(productKey) &&
        Number(item.id) === Number(productId) &&
        String(item.properties?.["_warranty.variant_id"]) === String(warrantyId)
      ) || null;

      const warrantyInCart = productInCart && cartItems.find((item) =>
        String(item.properties?.["_warranty_uuid"]) === String(productInCart.properties?._warranty_uuid) &&
        Number(item.properties?.["_warranty.associated_variant_id"]) === Number(productId) &&
        Number(item.id) === Number(warrantyId)
      ) || null;

      return { productInCart, warrantyInCart };
    },

    // Finds another line of the same phone with no plan.
    productWithoutWarrantyInCart: async function (cartItems, productId, productKey) {
      return cartItems.find((item) => {
        const props = item.properties || {};
        return String(item.key) !== String(productKey) &&
          Number(item.id) === Number(productId) &&
          (props["_warranty_uuid"] === undefined || props["_warranty.variant_id"] === undefined);
      }) || null;
    },

    // True for any line of the protection plan product.
    isPlanLine: function (item) {
      return this.WARRANTY_PRODUCT_ID != null && Number(item?.product_id) === Number(this.WARRANTY_PRODUCT_ID);
    },

    // True for a plan line that points to a phone.
    isWarrantyLine: function (item) {
      const props = item?.properties || {};
      return props["_warranty_uuid"] != null && props["_warranty.associated_variant_id"] != null;
    },

    // True for a phone line that points to a plan.
    isPairedProductLine: function (item) {
      const props = item?.properties || {};
      return props["_warranty_uuid"] != null &&
        props["_warranty.variant_id"] != null &&
        props["_warranty.associated_variant_id"] == null;
    },

    // Finds a phone's plan, or a plan's phone.
    findPairPartner: function (items, line) {
      const uuid = line?.properties?.["_warranty_uuid"];
      if (uuid == null) return null;
      const wantsPlan = this.isPairedProductLine(line);
      return items.find((item) => item.key !== line.key &&
        String(item.properties?.["_warranty_uuid"]) === String(uuid) &&
        (wantsPlan ? this.isWarrantyLine(item) : this.isPairedProductLine(item))) || null;
    },

    // Drops the plan link from a phone line's properties.
    stripWarrantyProps: function (properties) {
      const pairingKeys = ["_warranty_uuid", "_warranty.variant_id"];
      return Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !pairingKeys.includes(key)));
    },

    // Writes a prefixed console error that Datadog picks up.
    // It never throws, so it cannot break the cart.
    reportError: function (where, error, details) {
      try {
        const context = {
          page: window.location.pathname,
          theme_version: this.VERSION,
          ...details,
          stack: String(error?.stack || "").slice(0, 600) || undefined,
        };
        const reason = error ? `: ${error.message || error}` : "";
        console.error(`${this.CONSOLE_PREFIX} ${where}${reason} ${JSON.stringify(context)}`);
      } catch (e) {
        try { console.error(`${this.CONSOLE_PREFIX} ${where}`); } catch (ignored) { /* no console */ }
      }
    },

    // Short view of one cart line for error reports.
    lineSummary: function (item) {
      const props = item?.properties || {};
      return {
        key: item?.key,
        variant_id: item?.id,
        product_id: item?.product_id,
        quantity: item?.quantity,
        warranty_uuid: props["_warranty_uuid"],
        plan_variant_id: props["_warranty.variant_id"],
        associated_variant_id: props["_warranty.associated_variant_id"],
      };
    },

    // Short view of the whole cart for error reports.
    cartSummary: function (items = []) {
      const { updates, untag } = this.planRepairs(items);
      return {
        lines: items.slice(0, 50).map((line) => this.lineSummary(line)),
        pairing_ok: Object.keys(updates).length + untag.length === 0,
      };
    },

    // Queue and count of cart changes running in this tab.
    _cartLock: Promise.resolve(),
    _cartBusy: 0,

    // Runs cart changes one by one and blocks checkout.
    // Tells other tabs when the cart really changed.
    withCartLock: function (action) {
      this._cartBusy += 1;
      this.setCheckoutBlocked(true);
      const task = () => this.withTabLock(async () => {
        this._cartChanged = false;
        try {
          return await action();
        } finally {
          if (this._cartChanged) this.publishCartChange?.();
        }
      });
      const run = this._cartLock.then(task, task);
      this._cartLock = run.catch(() => {});
      run.catch(() => {}).finally(() => {
        this._cartBusy -= 1;
        if (this._cartBusy === 0) this.setCheckoutBlocked(false);
      });
      return run;
    },

    // Takes the cross-tab lock before running a cart change.
    // A failed change keeps its marker; the next page re-checks.
    withTabLock: async function (action) {
      const runWithMarker = async () => {
        const cutShort = this.wasChangeCutShort();
        const interrupted = this.wasChangeInterrupted();
        this.markChangeRunning(true);
        if (interrupted) await this.repairCart().catch(() => null);
        const result = await action();
        this.markChangeRunning(false);
        if (cutShort) this.openRecheckWindow();
        return result;
      };
      if (!navigator.locks?.request) return runWithMarker();

      const waitLimit = new AbortController();
      const timer = setTimeout(() => waitLimit.abort(), this.CART_LOCK_TIMEOUT_MS);
      let acquired = false;
      try {
        return await navigator.locks.request("gz-cart", { signal: waitLimit.signal }, () => {
          acquired = true;
          clearTimeout(timer);
          return runWithMarker();
        });
      } catch (error) {
        if (acquired) throw error;
        this.reportError("cart lock busy in another tab, continuing", error);
        return this.runWithoutLock(action);
      }
    },

    // Runs a change after the lock wait ends.
    // Leaves the other tab's marker alone; it may be live.
    runWithoutLock: async function (action) {
      const result = await action();
      this.openRecheckWindow();
      return result;
    },

    // Sets or clears a marker while a change runs.
    markChangeRunning: function (running) {
      try {
        if (running) window.localStorage.setItem("gz_cart_change", String(Date.now()));
        else window.localStorage.removeItem("gz_cart_change");
      } catch (e) { /* storage blocked */ }
    },

    // True if the last cart change never finished.
    wasChangeCutShort: function () {
      try { return !!window.localStorage.getItem("gz_cart_change"); } catch (e) { return false; }
    },

    // Starts a short period where pages re-check the cart.
    // A request from the page we left may land late.
    openRecheckWindow: function () {
      try { window.localStorage.setItem("gz_cart_recheck_until", String(Date.now() + this.RECHECK_WINDOW_MS)); } catch (e) { /* storage blocked */ }
    },

    // True if a change was cut short or re-checked.
    wasChangeInterrupted: function () {
      try {
        const recheckUntil = Number(window.localStorage.getItem("gz_cart_recheck_until"));
        return this.wasChangeCutShort() || recheckUntil > Date.now();
      } catch (e) { return false; }
    },

    // True on the cart page.
    isCartPage: function () {
      return !!document.querySelector("#main-cart-items");
    },

    // Disables checkout so a half-done plan change cannot reach it.
    // Unblocking restores the store's own state, like an empty cart.
    setCheckoutBlocked: function (blocked) {
      document.querySelectorAll('#checkout, button[name="checkout"]').forEach((button) => {
        if (blocked) {
          if (!("gzWasDisabled" in button.dataset)) button.dataset.gzWasDisabled = String(button.disabled);
          button.disabled = true;
          button.setAttribute("aria-disabled", "true");
        } else if ("gzWasDisabled" in button.dataset) {
          button.disabled = button.dataset.gzWasDisabled === "true";
          button.setAttribute("aria-disabled", String(button.disabled));
          delete button.dataset.gzWasDisabled;
        }
      });
      document.querySelectorAll(".cart__dynamic-checkout-buttons").forEach((element) => {
        element.style.pointerEvents = blocked ? "none" : "";
        element.toggleAttribute("inert", blocked);
        element.setAttribute("aria-disabled", String(blocked));
      });
    },

    // Redraws the cart page from Shopify's cart without changing it.
    refreshCartSections: async function () {
      const data = await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: {} });
      this.renderCartSections(data);
      return { ...data, gzRefreshedOnly: true };
    },

    // Removes the plan link from a phone line.
    // change.js cannot clear properties, so keep a marker.
    untagProductLine: async function (line) {
      const cleaned = this.stripWarrantyProps(line.properties);
      const properties = Object.keys(cleaned).length ? cleaned : { "_gz_plan_removed": "1" };
      return this.updateCart(this.CART_CHANGE_ENDPOINT, { id: line.key, quantity: line.quantity, properties });
    },

    // Works out fixes so each phone has one matching plan.
    // Sold-out phones and stray plans are removed too.
    planRepairs: function (items, stockByKey = {}) {
      const updates = {};
      const untag = [];
      const phones = items.filter((item) => this.isPairedProductLine(item));
      const plans = items.filter((item) => this.isWarrantyLine(item));
      const keptPlanKeys = new Set();

      for (const phone of phones) {
        const stock = stockByKey[phone.key];
        if (Number.isFinite(stock) && stock < 1) {
          updates[phone.key] = 0;
          continue;
        }

        const matchingPlans = plans.filter((plan) =>
          String(plan.properties["_warranty_uuid"]) === String(phone.properties["_warranty_uuid"]) &&
          String(plan.properties["_warranty.associated_variant_id"]) === String(phone.id) &&
          String(plan.id) === String(phone.properties["_warranty.variant_id"]));
        if (!matchingPlans.length) { untag.push(phone); continue; }

        const keptPlan = matchingPlans[0];
        keptPlanKeys.add(keptPlan.key);

        let quantity = phone.quantity;
        if (Number.isFinite(stock) && quantity > stock) {
          quantity = stock;
          updates[phone.key] = quantity;
        }
        if (keptPlan.quantity !== quantity) updates[keptPlan.key] = quantity;
      }

      for (const plan of plans) {
        if (!keptPlanKeys.has(plan.key) && updates[plan.key] === undefined) updates[plan.key] = 0;
      }
      for (const item of items) {
        if (this.isPlanLine(item) && !this.isWarrantyLine(item) && updates[item.key] === undefined) updates[item.key] = 0;
      }
      return { updates, untag };
    },

    // Applies the fixes and reports them.
    // Returns null when the cart was already fine.
    repairCart: async function ({ stock = {}, cart, plan, trigger } = {}) {
      cart = cart || await this.fetchCart();
      const { updates, untag } = plan || this.planRepairs(cart.items || [], stock);
      let latest = null;
      let items = cart.items || [];

      if (Object.keys(updates).length) {
        latest = await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates });
        items = latest.items;
      }
      for (const line of untag) {
        const current = items.find((item) => item.key === line.key);
        if (!current) continue;
        latest = await this.untagProductLine(current);
        items = "item_count" in latest ? latest.items : (await this.fetchCart()).items;
      }
      if (latest) {
        this.reportError("pairing repaired", null, { trigger, repairs: updates, untagged: untag.map((line) => this.lineSummary(line)) });
      }
      return latest;
    },

    // Repairs the cart, then returns something the page can draw.
    repairForPage: async function (options) {
      const latest = await this.repairCart(options);
      return latest?.sections || !this.isCartPage() ? latest : this.refreshCartSections();
    },

    // Checks the cart and repairs it if it is broken.
    // Returns null when nothing needed fixing.
    checkCart: async function (stock = {}, trigger) {
      return this.withCartLock(async () => {
        const cart = await this.fetchCart();
        const plan = this.planRepairs(cart.items || [], stock);
        const needsRepair = Object.keys(plan.updates).length > 0 || plan.untag.length > 0;
        return needsRepair ? this.repairForPage({ stock, cart, plan, trigger }) : null;
      });
    },

    // A failed-looking request may still have worked.
    // Reports it, then re-syncs the cart with Shopify.
    recoverFromError: async function (error, where) {
      this.reportError(where, error, { location: window.location.href });
      try { return await this.repairForPage(); } catch (e) { return this.refreshCartSections().catch(() => null); }
    },

    // Cart Add: adds the plan first, then links the phone.
    // Undoing the plan never touches the phone.
    addWarranty: async function (productId, productKey, warrantyId) {
      return this.withCartLock(async () => {
        try {
          const cart = await this.fetchCart();
          const cartItems = cart.items || [];
          const phone = cartItems.find((item) => item.key === productKey);
          if (!phone) return this.refreshCartSections();

          const { productInCart, warrantyInCart } = await this.productHasWarrantyInCart(cartItems, productId, productKey, warrantyId);
          if (productInCart && warrantyInCart) {
            const quantity = productInCart.quantity + phone.quantity;
            return await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: {
              [phone.key]: 0,
              [productInCart.key]: quantity,
              [warrantyInCart.key]: quantity,
            } });
          }

          const oldPlan = this.findPairPartner(cartItems, phone);
          const uuid = this.generateUUID();

          const added = await this.addToCart({ items: [{
            id: warrantyId,
            quantity: phone.quantity,
            properties: { "_warranty_uuid": uuid, "_warranty.associated_variant_id": phone.id },
          }] });

          let result;
          try {
            result = await this.updateCart(this.CART_CHANGE_ENDPOINT, {
              id: phone.key,
              quantity: phone.quantity,
              properties: { ...phone.properties, "_warranty_uuid": uuid, "_warranty.variant_id": warrantyId },
            });
          } catch (error) {
            const planLine = (added.items || []).find((item) => String(item.properties?.["_warranty_uuid"]) === uuid);
            if (planLine) await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: { [planLine.key]: 0 } }).catch(() => null);
            return await this.recoverFromError(error, "addWarranty() flow: tagging the phone failed");
          }

          if (oldPlan) result = await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: { [oldPlan.key]: 0 } });
          return result;
        } catch (error) {
          return await this.recoverFromError(error, "addWarranty() flow");
        }
      });
    },

    // Cart page Remove: drops the plan, then unlinks the phone.
    removeWarranty: async function (productId, productKey, warrantyId) {
      return this.withCartLock(async () => {
        try {
          const cart = await this.fetchCart();
          const cartItems = cart.items || [];
          const phone = cartItems.find((item) => item.key === productKey);
          if (!phone) return this.refreshCartSections();

          const plan = this.findPairPartner(cartItems, phone);
          const freePhone = await this.productWithoutWarrantyInCart(cartItems, productId, productKey);

          if (freePhone) {
            const updates = { [phone.key]: 0, [freePhone.key]: freePhone.quantity + phone.quantity };
            if (plan) updates[plan.key] = 0;
            return await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates });
          }

          if (plan) await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: { [plan.key]: 0 } });
          const fresh = (await this.fetchCart()).items.find((item) => item.key === phone.key);
          return fresh ? await this.untagProductLine(fresh) : await this.repairForPage();
        } catch (error) {
          return await this.recoverFromError(error, "removeWarranty() flow");
        }
      });
    },

    // Product page add with a plan; throws if it fails.
    // Adding the same pair again raises both quantities.
    addProductAndWarranty: async function (productId, warrantyId, properties) {
      return this.withCartLock(async () => {
        try {
          const cart = await this.fetchCart();
          const cartItems = cart.items || [];
          const samePair = cartItems.find((item) => Number(item.id) === Number(productId) &&
            String(item.properties?.["_warranty.variant_id"]) === String(warrantyId) &&
            this.findPairPartner(cartItems, item));

          if (samePair) {
            const plan = this.findPairPartner(cartItems, samePair);
            const changed = await this.updateCart(this.CART_CHANGE_ENDPOINT, { id: samePair.key, quantity: samePair.quantity + 1 });
            const accepted = (changed.items || []).find((item) => item.key === samePair.key)?.quantity ?? samePair.quantity;
            return await this.updateCart(this.CART_UPDATE_ENDPOINT, { updates: { [plan.key]: accepted } });
          }

          const uuid = this.generateUUID();
          return await this.addToCart({ items: [
            { id: productId, quantity: 1, properties: { ...properties, "_warranty_uuid": uuid, "_warranty.variant_id": warrantyId } },
            { id: warrantyId, quantity: 1, properties: { "_warranty_uuid": uuid, "_warranty.associated_variant_id": productId } },
          ] });
        } catch (error) {
          this.reportError("addProductAndWarranty() flow", error, { location: window.location.href, productId, warrantyId, properties });
          await this.repairForPage().catch(() => null);
          throw error;
        }
      });
    },

    // The cart sections Shopify re-renders after a change.
    getCartSectionsToRender: function () {
      return [
        { selector: "#site-main-header", element: "#cart-icon-bubble" },
        { selector: "#main-cart-items" },
        { selector: "#main-cart-footer" },
      ];
    },

    // Collects the section ids Shopify needs to render them.
    getSectionIds: function () {
      return this.getCartSectionsToRender()
        .map(({ selector }) => document.querySelector(selector)?.dataset.id || null)
        .filter(Boolean);
    },

    // Parses the response without running it.
    // Returns the section's content as nodes.
    getSectionNodes: function (html, selector) {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const element = parsed.querySelector(selector);
      return Array.from((element || parsed.body).childNodes).map((node) => document.importNode(node, true));
    },

    // Puts the fresh cart HTML from Shopify into the page.
    renderCartSections: function (data) {
      this.getCartSectionsToRender().forEach(({ selector, element }) => {
        const sectionElement = document.querySelector(selector);
        if (!sectionElement) return console.warn(`${this.CONSOLE_PREFIX} Element not found for selector: ${selector}`);

        const sectionId = sectionElement.dataset.id;
        if (!sectionId) return console.warn(`${this.CONSOLE_PREFIX} No data-id attribute found for: ${selector}`);

        const sectionHTML = data.sections?.[sectionId];
        if (!sectionHTML) return console.warn(`${this.CONSOLE_PREFIX} No section HTML returned for: ${sectionId}`);

        const targetSelector = element || selector;
        const targetElement = element ? document.querySelector(element) : sectionElement;
        if (!targetElement) return console.warn(`${this.CONSOLE_PREFIX} Target element not found: ${targetSelector}`);

        targetElement.replaceChildren(...this.getSectionNodes(sectionHTML, targetSelector));
      });
      this.showEmptyCartState(data);
      if (this._cartBusy) this.setCheckoutBlocked(true);
    },

    // Shows the empty cart message for an empty cart.
    // Only the cart page has it; other pages do nothing.
    showEmptyCartState: function (data) {
      const page = document.querySelector(".gz-cart-items");
      if (!page || !Number.isFinite(data?.item_count)) return;
      page.classList.toggle("is-empty", data.item_count === 0);
    },

    // Tells screen readers what changed in the cart.
    announceCartUpdate: function (message) {
      const region = document.getElementById("gz-cart-live-region");
      if (!region) return;
      region.textContent = "";
      requestAnimationFrame(() => { region.textContent = message; });
    },

    // Shows or hides skeleton loaders inside the given targets.
    toggleSkeletonLoaders: function (loading, targets = []) {
      if (!Array.isArray(targets)) return;
      targets.forEach((target) => {
        const parent = typeof target === "string" ? document.querySelector(target) : target;
        parent?.querySelectorAll(".gz-skeleton-loader").forEach((skeleton) => {
          skeleton.style.display = loading ? "flex" : "none";
        });
      });
    },

    // Makes a unique id, with a fallback for old browsers.
    generateUUID: function () {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        return (char === "x" ? random : (random & 0x3 | 0x8)).toString(16);
      });
    },

    // Waits the given number of milliseconds.
    delay: function (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
  });

  if (!window.customElements.get("gz-warranty")) {
    // Protection plan controls on the product and cart pages.
    class GzWarranty extends HTMLElement {
      constructor() {
        super();
        this.form = null;
        this.radioButtons = [];
        this.isLoading = false;
      }

      // Wires up the buttons and listens for the selected product.
      connectedCallback() {
        this.init();
        this.listenForSelection();
      }

      // Stops listening for product changes.
      disconnectedCallback() {
        document.removeEventListener("gz:selection", this.onSelection);
      }

      // Finds the buttons and radios inside this block.
      init() {
        this.addBtns = this.querySelectorAll("button[data-gz-warranty-add]");
        this.removeBtns = this.querySelectorAll("button[data-gz-warranty-remove]");
        this.form = this.querySelector("[gz-warranty-form]");
        this.radioButtons = this.form ? this.form.querySelectorAll('input[type="radio"][name="warranty"]') : [];
        this.attachEventListeners();
      }

      // Hooks the plan buttons and radios to their handlers.
      attachEventListeners() {
        this.addBtns.forEach((btn) => btn.addEventListener("click", (e) => this.handleAddingWarranty(e)));
        this.removeBtns.forEach((btn) => btn.addEventListener("click", (e) => this.handleRemovingWarranty(e)));
        this.radioButtons.forEach((radio) => radio.addEventListener("change", (e) => this.handlePlanOptionChange(e)));
      }

      // Remembers which plan the customer picked on the product page.
      handlePlanOptionChange(event) {
        const selectedValue = this.parseValue(event.target.value);
        window.GzTheme.GZ_WARRANTY = selectedValue;
      }

      // Offers the selected product's own plan tier.
      listenForSelection() {
        if (!this.form) return;
        this.onSelection = (event) => this.updatePlanOption(event.detail);
        document.addEventListener("gz:selection", this.onSelection);
        if (window.GzTheme.SELECTION) this.updatePlanOption(window.GzTheme.SELECTION);
      }

      // Shows that product's plan and price, or hides the plan.
      updatePlanOption({ product, addedVariantId, warrantySku }) {
        const option = this.form.querySelector('label[for="protection-plan"]');
        const radio = this.form.querySelector("#protection-plan");
        if (!option || !radio) return;
        try {
          this.tiers = this.tiers || JSON.parse(this.querySelector("script[data-gz-plan-tiers]")?.textContent);
        } catch (error) {
          window.GzTheme.reportError("plan tiers unreadable", error, { product_handle: product?.handle });
          return;
        }

        const tier = this.tiers[String(warrantySku || "").toLowerCase()];
        option.hidden = !tier;
        this.hidden = !tier;
        if (tier) {
          radio.value = tier.id;
          const price = option.querySelector(".gz-warranty__option-price");
          if (price) price.textContent = tier.price;
        } else if (radio.checked) {
          radio.checked = false;
          const freePlan = this.form.querySelector("#warranty-none");
          if (freePlan) freePlan.checked = true;
        }

        window.GzTheme.GZ_WARRANTY = this.getSelectedValue();
        if (warrantySku && !tier) {
          window.GzTheme.reportError("no protection plan tier for the selected product", null, {
            product_handle: product?.handle,
            warranty_sku: warrantySku,
            added_variant_id: addedVariantId,
          });
        }
      }

      // Turns a radio value into a variant id or null.
      parseValue(value) {
        const parsed = parseInt(value, 10);
        return !value || value === "none" || isNaN(parsed) ? null : parsed;
      }

      // The variant id of the plan currently chosen.
      getSelectedValue() {
        const selected = this.form.querySelector('input[type="radio"][name="warranty"]:checked');
        return selected ? this.parseValue(selected.value) : null;
      }

      // Reads the plan and phone ids from a button.
      readButton(button, planAttribute) {
        return {
          warrantyId: parseInt(button.getAttribute(planAttribute)),
          productId: parseInt(button.getAttribute("data-gz-associated-item-id")),
          productKey: button.getAttribute("data-gz-associated-item-key"),
          title: button.getAttribute("data-gz-warranty-title") || "Protection plan",
        };
      }

      // Draws the result and tells screen readers what happened.
      showResult(data, title, doneText) {
        const GZ = window.GzTheme;
        if (data?.gzRefreshedOnly) {
          GZ.announceCartUpdate(window.cartStrings?.cartUpdated || "Your cart was updated. Please review it and try again.");
        } else if (data) {
          GZ.renderCartSections(data);
          GZ.announceCartUpdate(`${title} ${doneText}`);
        }
      }

      // Cart Add button: adds the plan, ignoring double clicks.
      async handleAddingWarranty(event) {
        event.preventDefault();
        if (this.isLoading) return;
        const { warrantyId, productId, productKey, title } = this.readButton(event.currentTarget, "data-gz-warranty-add");
        this.setLoading(true);
        try {
          const data = await window.GzTheme.addWarranty(productId, productKey, warrantyId);
          this.showResult(data, title, window.cartStrings?.addedToCart || "added to cart");
        } catch (error) {
          window.GzTheme.reportError("add protection plan click", error, { productId, warrantyId });
        } finally {
          this.setLoading(false);
        }
      }

      // Cart Remove button: removes the plan, ignoring double clicks.
      async handleRemovingWarranty(event) {
        event.preventDefault();
        if (this.isLoading) return;
        const { warrantyId, productId, productKey, title } = this.readButton(event.currentTarget, "data-gz-warranty-remove");
        this.setLoading(true);
        try {
          const data = await window.GzTheme.removeWarranty(productId, productKey, warrantyId);
          this.showResult(data, title, window.cartStrings?.removedFromCart || "removed from cart");
        } catch (error) {
          window.GzTheme.reportError("remove protection plan click", error, { productId, warrantyId });
        } finally {
          this.setLoading(false);
        }
      }

      // Shows skeleton loaders while a plan change runs.
      setLoading(isLoading) {
        this.isLoading = isLoading;
        window.GzTheme.toggleSkeletonLoaders(isLoading, [this, "#main-cart-footer"]);
      }
    }

    window.customElements.define("gz-warranty", GzWarranty);
  }

  if (!window.customElements.get("gz-cart-item")) {
    // Quantity and remove controls for one cart line.
    class GzCartItem extends HTMLElement {
      constructor() {
        super();
        this.cartItems = document.querySelector(".gz-cart-items");
        this.itemId = this.closest(".cart-item")?.id || null;
      }

      // Finds the controls and hooks up their events.
      connectedCallback() {
        this.input = this.querySelector('input[name="quantity"]');
        this.minusBtn = this.querySelector('button[name="minus"]');
        this.plusBtn = this.querySelector('button[name="plus"]');
        this.removeBtn = this.querySelector('button[name="remove"]');
        if (!this.input) return;

        this.itemKey = this.input.dataset.itemKey;
        this.minusBtn?.addEventListener("click", (e) => this.handleDecrease(e));
        this.plusBtn?.addEventListener("click", (e) => this.handleIncrease(e));
        this.removeBtn?.addEventListener("click", (e) => this.handleRemove(e));
        this.input.addEventListener("change", (e) => this.handleInputChange(e));
      }

      get min() { return parseInt(this.input.min) || 1; }
      get max() { return this.input.max ? parseInt(this.input.max) : Infinity; }
      get step() { return parseInt(this.input.step) || 1; }

      // Minus button: lowers the quantity by one step.
      handleDecrease(event) {
        event.preventDefault();
        const current = parseInt(this.input.value);
        if (current > this.min) this.setQuantity(current - this.step);
      }

      // Plus button: raises the quantity by one step.
      handleIncrease(event) {
        event.preventDefault();
        const current = parseInt(this.input.value);
        if (current < this.max) this.setQuantity(current + this.step);
      }

      // Remove button: sets the quantity to zero.
      handleRemove(event) {
        event.preventDefault();
        this.performUpdate(0);
      }

      // Typed quantity: keeps it between the min and max.
      handleInputChange(event) {
        this.setQuantity(Math.min(Math.max(parseInt(event.target.value), this.min), this.max));
      }

      // Shows the new quantity and saves it.
      setQuantity(quantity) {
        this.input.value = quantity;
        this.performUpdate(quantity);
      }

      // Reads the cart fresh, as page line keys go stale.
      async applyQuantity(quantity) {
        const GZ = window.GzTheme;
        return GZ.withCartLock(async () => {
          try {
            const cart = await GZ.fetchCart();
            const items = cart.items || [];
            const line = items.find((item) => item.key === this.itemKey);
            if (!line) return await GZ.refreshCartSections();

            const plan = GZ.isPairedProductLine(line) ? GZ.findPairPartner(items, line) : null;

            if (quantity > line.quantity) return await this.increaseQuantity(line, plan, quantity);

            const updates = { [line.key]: quantity };
            if (plan) updates[plan.key] = quantity;
            return await GZ.updateCart(GZ.CART_UPDATE_ENDPOINT, { updates });
          } catch (error) {
            return await GZ.recoverFromError(error, "GzCartItem quantity change");
          }
        });
      }

      // The store may accept less than asked for.
      // The plan follows whatever quantity it accepted.
      async increaseQuantity(line, plan, quantity) {
        const GZ = window.GzTheme;
        let result = null;
        try {
          result = await GZ.updateCart(GZ.CART_CHANGE_ENDPOINT, { id: line.key, quantity });
        } catch (error) { /* the store may accept less than requested */ }
        const fresh = result || await GZ.fetchCart();
        const accepted = (fresh.items || []).find((item) => item.key === line.key)?.quantity ?? line.quantity;

        if (plan) return await GZ.updateCart(GZ.CART_UPDATE_ENDPOINT, { updates: { [plan.key]: accepted } });
        return result?.sections ? result : await GZ.refreshCartSections();
      }

      // Saves the quantity and redraws the cart, with loaders.
      async performUpdate(quantity) {
        const GZ = window.GzTheme;
        this.setLoading(true);
        try {
          const data = await this.applyQuantity(quantity);
          if (!data) return;

          if (data.item_count === 0) this.handleEmptyCart();
          const accepted = (data.items || []).find((item) => item.key === this.itemKey)?.quantity ?? quantity;
          this.announceChange(data, accepted);
          GZ.renderCartSections(data);
        } catch (error) {
          GZ.reportError("GzCartItem quantity change", error, { location: window.location.href, payload: { [this.itemKey]: quantity } });
          await GZ.refreshCartSections().catch(() => null);
        } finally {
          this.setLoading(false);
        }
      }

      // Tells screen readers what happened to the line.
      announceChange(data, quantity) {
        const GZ = window.GzTheme;
        const title = this.removeBtn?.getAttribute("data-item-title") || "Item";
        if (data.gzRefreshedOnly) {
          GZ.announceCartUpdate(window.cartStrings?.cartUpdated || "Your cart was updated. Please review it and try again.");
        } else if (quantity === 0) {
          GZ.announceCartUpdate(`${title} ${window.cartStrings?.removedFromCart || "removed from cart"}`);
        } else {
          GZ.announceCartUpdate(`${title} ${window.cartStrings?.quantityUpdated || "quantity updated to"} ${quantity}`);
        }
      }

      // Shows skeleton loaders on this line and the totals.
      setLoading(isLoading) {
        window.GzTheme.toggleSkeletonLoaders(isLoading, [`#${this.itemId}`, "#main-cart-footer"]);
      }

      // Marks the cart page as empty.
      handleEmptyCart() {
        this.cartItems?.classList.add("is-empty");
      }
    }

    customElements.define("gz-cart-item", GzCartItem);
  }
});
